export type SearchableService = {
  name?: string;
  service_name?: string;
  service_about?: string;
  provider?: string;
  category_name?: string;
  categoryName?: string;
  service_catageory?: {
    service_catageory_name?: string;
    name?: string;
    service_catageory_about?: string;
    service_catageory_description?: string;
  } | null;
};

export type SearchableCategory = {
  name?: string;
  service_catageory_name?: string;
  service_catageory_about?: string;
  service_catageory_description?: string;
};

const QUERY_EXPANSIONS: Array<{ test: RegExp; words: string[] }> = [
  {
    test: /(clean|cleaning|deep clean|home clean|house clean|maid|housekeeping|mane|swacch|safai|saaf|safayi|स्वच्छ|सफाई|ಕ್ಲೀನ್|ಸ್ವಚ್ಛ|சுத்த|శుభ్ర)/i,
    words: ['clean', 'cleaning', 'deep', 'home', 'house', 'sofa', 'bathroom', 'kitchen', 'maid', 'housekeeping'],
  },
  {
    test: /(ac|a c|a\/c|air condition|air conditioner|cooling|split ac|window ac|ಎಸಿ|ஏசி|ఏసీ)/i,
    words: ['ac', 'air', 'conditioner', 'cooling', 'install', 'repair', 'service'],
  },
  {
    test: /(plumb|plumber|plumbing|tap|pipe|leak|water leak|ನಳ|ಪೈಪ್|குழாய்|పైపు)/i,
    words: ['plumbing', 'plumber', 'tap', 'pipe', 'leak', 'water'],
  },
  {
    test: /(electric|electrical|electrician|fan|switch|wiring|light|current|ವಿದ್ಯುತ್|எலக்ட்ரிக்|ఎలక్ట్రిక్)/i,
    words: ['electric', 'electrical', 'electrician', 'fan', 'switch', 'wiring', 'light'],
  },
  {
    test: /(pest|cockroach|termite|mosquito|bed bug|bug|ಕೀಟ|பூச்சி|పురుగు)/i,
    words: ['pest', 'cockroach', 'termite', 'mosquito', 'bug'],
  },
  {
    test: /(beauty|salon|spa|facial|hair|makeup|parlour|ಪಾರ್ಲರ್|சலூன்|సెలూన్)/i,
    words: ['beauty', 'salon', 'spa', 'facial', 'hair', 'makeup', 'parlour'],
  },
  {
    test: /(paint|painting|wall|ಬಣ್ಣ|பெயிண்ட்|పెయింట్)/i,
    words: ['paint', 'painting', 'wall', 'colour'],
  },
  {
    test: /(repair|fix|handyman|install|installation|service|riperi|ರಿಪೇರಿ|சரி|రిపేర్)/i,
    words: ['repair', 'fix', 'handyman', 'install', 'installation', 'service'],
  },
];

export function normalizeSearchText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFC')
    .toLocaleLowerCase('en-IN')
    .replace(/&/g, ' and ')
    .replace(/\ba\s*\/?\s*c\b/g, ' ac ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function searchTokens(value: unknown): string[] {
  return normalizeSearchText(value)
    .split(/\s+/)
    .filter((token) => token.length > 1);
}

export function expandSearchTokens(query: string): string[] {
  const normalized = normalizeSearchText(query);
  const tokens = new Set(searchTokens(normalized));
  for (const expansion of QUERY_EXPANSIONS) {
    if (expansion.test.test(query) || expansion.test.test(normalized)) {
      expansion.words.forEach((word) => tokens.add(normalizeSearchText(word)));
    }
  }
  return Array.from(tokens).filter(Boolean);
}

export function getServiceSearchText(service: SearchableService): string {
  return [
    service.name,
    service.service_name,
    service.service_about,
    service.provider,
    service.category_name,
    service.categoryName,
    service.service_catageory?.service_catageory_name,
    service.service_catageory?.name,
    service.service_catageory?.service_catageory_about,
    service.service_catageory?.service_catageory_description,
  ]
    .filter(Boolean)
    .join(' ');
}

export function getCategorySearchText(category: SearchableCategory): string {
  return [
    category.name,
    category.service_catageory_name,
    category.service_catageory_about,
    category.service_catageory_description,
  ]
    .filter(Boolean)
    .join(' ');
}

export function scoreSearchText(textValue: string, query: string): number {
  const q = normalizeSearchText(query);
  if (!q) return 1;

  const text = normalizeSearchText(textValue);
  const textTokens = searchTokens(text);
  const queryTokens = searchTokens(q);
  const expandedTokens = expandSearchTokens(query);
  let score = 0;

  if (text.includes(q)) score += 30;
  if (textTokens.includes(q)) score += 20;

  for (const token of queryTokens) {
    if (textTokens.includes(token)) score += token.length <= 2 ? 16 : 12;
    else if (token.length > 2 && text.includes(token)) score += 8;
  }

  for (const token of expandedTokens) {
    if (textTokens.includes(token)) score += token.length <= 2 ? 8 : 6;
    else if (token.length > 2 && text.includes(token)) score += 4;
  }

  return score;
}

export function filterAndRankBySearch<T>(
  items: T[] | undefined,
  query: string,
  getText: (item: T) => string,
): T[] {
  const list = items ?? [];
  if (!normalizeSearchText(query)) return list;

  return list
    .map((item, index) => ({ item, index, score: scoreSearchText(getText(item), query) }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((row) => row.item);
}
